import React from 'react'
import { useMsal } from '@azure/msal-react'
import {
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import SecurityIcon from '@material-ui/icons/Security'
import FavoriteIcon from '@material-ui/icons/Favorite'
import SearchIcon from '@material-ui/icons/Search'
import ListIcon from '@material-ui/icons/List'
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart'
import DraftIcon from '@material-ui/icons/Drafts'
import SettingsIcon from '@material-ui/icons/Settings'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import { useOverlayPreloader } from '../../../../hooks/use-overlay-preloader'
import { selectUser } from '../../../../store/auth/selectors'

type MenuForAuthenticatedProps = {
  closeDrawer: () => void
}

export const MenuForAuthenticated: React.FC<MenuForAuthenticatedProps> = (
  props
): React.ReactElement => {
  const { closeDrawer } = props
  const { t } = useTranslation()
  const { instance } = useMsal()
  const { setIsShowOverlayPreloader } = useOverlayPreloader()
  const user = useSelector(selectUser)

  /**
   * Handles user logout by showing preloader, closing drawer and redirecting to logout
   */
  const handleLogoutRedirect = () => {
    setIsShowOverlayPreloader(true)
    closeDrawer()
    instance.logoutRedirect()
  }

  /**
   * Icon mapping for menu items
   */
  const iconMap: Record<string, React.ReactElement> = {
    '/moderation': <SecurityIcon />,
    '/donate': <FavoriteIcon />,
    '/search': <SearchIcon />,
    '/my-listed-items': <ListIcon />,
    '/listed-items/buyer': <ShoppingCartIcon />,
    '/orders': <ShoppingCartIcon />,
    '/draft-items': <DraftIcon />,
    '/account': <SettingsIcon />,
  }

  /**
   * Creates a menu link item that navigates to the specified route
   */
  const menuLink = (to: string, label: string) => (
    <ListItem button to={to} component={Link} onClick={closeDrawer}>
      <ListItemIcon>{iconMap[to]}</ListItemIcon>
      <ListItemText>{label}</ListItemText>
    </ListItem>
  )

  /**
   * Creates a menu button item with custom click handler
   */
  const menuButton = (
    label: string,
    onClick: (e: React.MouseEvent) => void,
    icon?: React.ReactElement
  ) => (
    <ListItem button onClick={onClick}>
      {icon && <ListItemIcon>{icon}</ListItemIcon>}
      <ListItemText>{label}</ListItemText>
    </ListItem>
  )

  const menuItems = [
    user.role === 1 && {
      to: '/moderation',
      label: t('header.menu.moderation'),
    },
    { to: '/donate', label: t('header.menu.donate') },
    { to: '/search', label: t('header.menu.search') },
    { to: '/my-listed-items', label: t('header.menu.myListedItems') },
    { to: '/listed-items/buyer', label: t('header.menu.purchasedItems') },
    { to: '/orders', label: t('header.menu.myOrders') },
    { to: '/draft-items', label: t('header.menu.draftItems') },
    { to: '/account', label: t('header.menu.settings') },
  ].filter(Boolean) as Array<{ to: string; label: string }>

  return (
    <List>
      {menuItems.map((item) => menuLink(item.to, item.label))}
      <Divider />
      {menuButton(
        t('header.menu.logout'),
        handleLogoutRedirect,
        <ExitToAppIcon />
      )}
    </List>
  )
}
